import FacebookSvg from 'components/_common/Icons/SVGIcons/FacebookSvg';
import FFLLoginIconSvg from 'components/_common/Icons/SVGIcons/FFLLoginIconSvg';
import InstagramSvg from 'components/_common/Icons/SVGIcons/InstagramSvg';
import MoonSvg from 'components/_common/Icons/SVGIcons/MoonSvg';
import RumbleSvg from 'components/_common/Icons/SVGIcons/RumbleSvg';
import SunSvg from 'components/_common/Icons/SVGIcons/SunSvg';
import TwitterSvg from 'components/_common/Icons/SVGIcons/TwitterSvg';
import YouTubeSvg from 'components/_common/Icons/SVGIcons/YouTubeSvg';
import useDashboardLink from 'hooks/useDashboardLink';
import { t } from 'i18next';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { customerMainRoutes } from 'routes/customer/CustomerRoutes';
import {
  socialMediaUrlsSelector,
  appThemeSelector,
} from 'store/customer/selectors/CustomerGlobalSelectors';
import { setAppTheme } from 'store/customer/slices/CustomerGlobalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

const TopSection: React.FC = () => {
  const socialMediaUrls = useAppSelector(socialMediaUrlsSelector);
  const dispatch = useAppDispatch();
  const theme = useAppSelector(appThemeSelector);
  // FFL Login / FFL Dashboard / Admin Dashboard - same hook as the mobile side menu
  const { label: dashboardLinkLabel, path: dashboardLinkPath } = useDashboardLink();

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
    } else {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
    }
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    dispatch(setAppTheme(theme === 'dark' ? 'light' : 'dark'));
  };

  return (
    <div className="top-section">
      <div className="w-100 d-flex justify-content-between align-items-center gap-3">
        {/* Left side */}
        <div className="top-section__left">
          {/* plain text, not a link: it has no destination, and an href="#" only jumped the page to the top */}
          <span className="top-section__label">{t('why_gun_shops_join')}</span>
          <Link to={dashboardLinkPath} target="_self" reloadDocument>
            <FFLLoginIconSvg />
            {dashboardLinkLabel}
          </Link>
        </div>

        {/* Right side */}
        <div className="top-section__right">
          <div className="top-section__nav-links">
            <Link to={customerMainRoutes.fflListing.path}>{t('gun_shop_locator')}</Link>
            <a href="#about">{t('about')}</a>
            <Link to={customerMainRoutes.blogList.path}>{t('blog')}</Link>
          </div>
          <div className="top-section__social-icons">
            {/* YouTube */}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={socialMediaUrls?.mlgs_youtube_url ?? '#'}
              aria-label={t('youtube')}
            >
              <YouTubeSvg />
            </a>
            {/* Twitter/X */}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={socialMediaUrls?.mlgs_x_url ?? '#'}
              aria-label={t('twitter')}
            >
              <TwitterSvg width={14} height={14} />
            </a>
            {/* Instagram */}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={socialMediaUrls?.mlgs_instagram_url ?? '#'}
              aria-label={t('instagram')}
            >
              <InstagramSvg />
            </a>
            {/* Facebook */}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={socialMediaUrls?.mlgs_facebook_url ?? '#'}
              aria-label={t('facebook')}
            >
              <FacebookSvg />
            </a>
            {/* Rumble */}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={socialMediaUrls?.mlgs_rumble_url ?? '#'}
              aria-label={t('rumble')}
            >
              <RumbleSvg width={14} height={14} />
            </a>
          </div>

          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className={`top-section__theme-toggle ${theme === 'dark' ? 'is-dark' : 'is-light'}`}
            title={theme === 'dark' ? t('switch_to_light_mode') : t('switch_to_dark_mode')}
            aria-label="Toggle Theme"
          >
            <div className="theme-toggle-track">
              <span className="theme-toggle-icon moon-icon">
                <MoonSvg />
              </span>
              <span className="theme-toggle-icon sun-icon">
                <SunSvg />
              </span>
              <div className="theme-toggle-thumb" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopSection;
